import qs from 'qs';

import { definitions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import type {
  AdminSubmitClaimRequestBody,
  AdminSubmitClaimResponse,
  ListCampaignsResponse,
  PromoCampaignBody,
  PromoCampaignCreateResponse,
  PromoCampaignDeleteResponse,
  PromoCampaignReadResponse,
  PromoCampaignSearchParams,
  PromoCampaignSearchResponse,
  PromoCampaignUpdateBody,
  PromoCampaignUpdateResponse,
  TierChangeRequestBody,
  TierChangeResponse,
} from '~/types/loyaltyContract';

import { EmptyArray, arrayToObject } from '~/utils/arrayUtils';
import { isLuxLoyaltyEnabled } from '~/utils/luxLoyalty';

import { request } from './common';

type RequestOptions = {
  signal?: AbortSignal;
};

type GetLuxLoyaltyAccountResponse =
  paths['/api/lux-loyalty/account/me']['get']['responses']['200']['content']['application/json']['result']['accountSummary'];

type GetLuxLoyaltyProgramConfigResponse =
  paths['/api/lux-loyalty/program-configuration']['get']['responses']['200']['content']['application/json']['result'];

type GetLuxLoyaltyActivityLogResponse =
  paths['/api/lux-loyalty/account/activities']['get']['responses']['200']['content']['application/json'];

type GetLuxLoyaltyPendingPointsResponse =
  paths['/api/lux-loyalty/customer/{customerId}/order/{orderId}/pending-points']['get']['responses']['200']['content']['application/json'];

type AdminLuxLoyaltyGetAccrualsByOrderResponse =
  paths['/api/lux-loyalty/admin/earn/order/{orderId}']['get']['responses']['200']['content']['application/json'];

type AccountUpdateRegionRequest =
  paths['/api/lux-loyalty/account/region']['patch']['requestBody']['content']['application/json'];

type AccountUpdateRegionResponse =
  paths['/api/lux-loyalty/account/region']['patch']['responses']['200']['content']['application/json'];

type LuxLoyaltyAccountRegions = definitions['accountRegions'];

export type LuxLoyaltyAccrual = AdminLuxLoyaltyGetAccrualsByOrderResponse['result']['accruals'];

const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

function mapAccountSummaryToLuxLoyaltyAccount(
  accountSummary: GetLuxLoyaltyAccountResponse,
): App.LuxLoyaltyAccount | null {
  if (!accountSummary) {
    return null;
  }

  return {
    customerId: accountSummary.customerId,
    points: accountSummary.points,
    tier: accountSummary.tier,
    statusCredits: accountSummary.statusCredits,
    ibsMemberId: accountSummary.ibsMemberId,
    accountNumber: accountSummary.accountNumber ?? undefined,
    createdAt: accountSummary.createdAt ?? undefined,
    statusReviewDate: accountSummary.statusReviewDate ?? undefined,
    totalCreditsForTierRetention: accountSummary.totalCreditsForTierRetention ?? undefined,
    totalCreditsForTierUpgrade: accountSummary.totalCreditsForTierUpgrade ?? undefined,
    numberOfNights: accountSummary.numberOfNights,
    numberOfNightsForTierUpgrade: accountSummary.numberOfNightsForTierUpgrade ?? undefined,
    numberOfNightsForTierRetention: accountSummary.numberOfNightsForTierRetention ?? undefined,
    benefits: accountSummary.benefits.map(
      (benefit): App.LuxLoyaltyBenefit => ({
        code: benefit.code,
        description: benefit.description,
        details: {
          // convert 'unlimited' to infinity as it's a lot easier to work with numbers, not number | string
          remaining: benefit.details.remaining === 'unlimited' ? Infinity : benefit.details.remaining,
          limit: benefit.details.limit === 'unlimited' ? Infinity : benefit.details.limit,
          value: benefit.details.value
            ? {
                amount: benefit.details.value.amount,
                unit: benefit.details.value.unit,
                currency: benefit.details.value.currency,
              }
            : undefined,
        },
      }),
    ),
    region: accountSummary.region,
  };
}

async function getLuxLoyaltyAccount(customerId: string): Promise<App.LuxLoyaltyAccount | null> {
  if (!customerId || !isLuxLoyaltyEnabled()) {
    return null;
  }

  const queryParams = qs.stringify({ customerId });
  const uri = `${BASE_PATH}/account?${queryParams}`;

  const { result } = await request(uri, { method: 'GET' });
  const accountSummary: GetLuxLoyaltyAccountResponse = result.accountSummary;

  return mapAccountSummaryToLuxLoyaltyAccount(accountSummary);
}

/**
 * Fetch a Lux Loyalty account using the loyalty account number.
 */
async function getLuxLoyaltyAccountByNumber(accountNumber: number | string): Promise<App.LuxLoyaltyAccount | null> {
  if (!isLuxLoyaltyEnabled() || accountNumber == null) {
    return null;
  }

  const value = String(accountNumber).trim();
  const uri = `${BASE_PATH}/account/account-number/${value}`;

  const { result } = await request(uri, { method: 'GET' });
  const accountSummary: GetLuxLoyaltyAccountResponse = result.accountSummary;
  return mapAccountSummaryToLuxLoyaltyAccount(accountSummary);
}

function mapLuxLoyaltyProgramConfig(response: GetLuxLoyaltyProgramConfigResponse): App.LuxLoyaltyProgramConfig {
  return {
    programName: response.programName,
    currencyName: response.currencyName,
    statusCreditsName: response.statusCreditsName,
    tierLabel: response.tierLabel,
    tiers: arrayToObject(response.tiers, (tier) => tier.id),
  };
}

async function getLuxLoyaltyProgramConfig(region?: string): Promise<GetLuxLoyaltyProgramConfigResponse | null> {
  if (!isLuxLoyaltyEnabled()) {
    return null;
  }

  const query = qs.stringify({
    region,
  });

  const uri = `${BASE_PATH}/program-configuration?${query}`;

  const { result } = await request(uri, { method: 'GET' });
  return result;
}

async function getLuxLoyaltyOrderPendingPoints(
  customerId: string,
  orderId: string,
): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  if (!isLuxLoyaltyEnabled() || !customerId || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/customer/${customerId}/order/${orderId}/pending-points`;
  const response: GetLuxLoyaltyPendingPointsResponse = await request(uri, {
    method: 'GET',
  });

  return response.result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate,
      status: pendingPointData.status,
    };
  });
}

async function getLuxLoyaltyActivityLog(
  customerId: string,
  fromDate: string,
  toDate: string,
): Promise<App.LuxLoyaltyActivityLog | null> {
  const queryParams = qs.stringify({ customerId, fromDate, toDate });
  const uri = `${BASE_PATH}/account/activities?${queryParams}`;

  const response: GetLuxLoyaltyActivityLogResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.activities?.map((activity) => ({
    id: activity.id,
    activityDate: activity.activityDate,
    activityType: activity.activityType,
    activityDescription: activity.activityDescription,
    statusCredits: activity.statusCredits,
    points: activity.basePoints,
    numberOfNights: activity.numNights,
    currencyAmount: activity.currencyAmount,
    currency: activity.currency,
    orderId: activity.orderId,
    itemId: activity.itemId,
  }));
}

async function getLuxLoyaltyCustomerPendingPoints(customerId: string): Promise<Array<App.LuxLoyaltyPendingPoint>> {
  const uri = `${BASE_PATH}/customer/${customerId}/pending-points`;
  const response: GetLuxLoyaltyPendingPointsResponse = await request(uri, { method: 'GET' });
  const { result } = response;

  return result.pendingPoints.map((pendingPointData): App.LuxLoyaltyPendingPoint => {
    const basePoints = pendingPointData.points;
    const bonusPoints = pendingPointData.bonusPoints;

    return {
      id: pendingPointData.id,
      createdAt: pendingPointData.createdAt,
      itemId: pendingPointData.itemId,
      orderId: pendingPointData.orderId,
      basePoints,
      bonusPoints,
      points: basePoints + bonusPoints,
      statusCredits: pendingPointData.statusCredits,
      bonusStatusCredits: pendingPointData.bonusStatusCredits,
      pointsEarnDate: pendingPointData.pointsEarnDate ?? undefined,
      status: pendingPointData.status,
    };
  });
}

async function getLuxLoyaltySuccessfulAccrualOnOrder(orderId: string): Promise<Array<App.LuxLoyaltyAccrualItem>> {
  if (!isLuxLoyaltyEnabled() || !orderId) {
    return EmptyArray;
  }

  const uri = `${BASE_PATH}/admin/earn/order/${orderId}`;
  const response: AdminLuxLoyaltyGetAccrualsByOrderResponse = await request(uri, { method: 'GET' });
  return response.result.accruals.map((accrual) => ({
    itemId: accrual.itemId,
    points: accrual.points,
    statusCredits: accrual.statusCredits,
    dateEarned: accrual.dateEarned,
  }));
}

/**
 * Updates the region for a Lux Loyalty account
 * @param customerId - The customer ID whose region needs to be updated
 * @param region - The new region code (e.g., 'SG', 'NZ', 'AU')
 * @returns The updated account summary response or null if Lux Loyalty is not enabled
 * @throws {Error} When Lux Loyalty is not enabled or customer ID is missing
 */
async function updateLuxLoyaltyAccountRegion(
  customerId: string,
  region: AccountUpdateRegionRequest['region'],
): Promise<AccountUpdateRegionResponse | null> {
  // Validate that Lux Loyalty is enabled and customer ID is provided
  if (!isLuxLoyaltyEnabled() || !customerId) {
    throw new Error('Lux Loyalty is not enabled or customer ID is missing');
  }

  const uri = `${BASE_PATH}/account/region`;
  const payload: AccountUpdateRegionRequest = {
    customerId,
    region,
  };

  // Make PATCH request to update the account region
  const response: AccountUpdateRegionResponse = await request(uri, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  return response;
}

export {
  BASE_PATH,
  getLuxLoyaltyAccount,
  getLuxLoyaltyAccountByNumber,
  getLuxLoyaltyProgramConfig,
  mapLuxLoyaltyProgramConfig,
  getLuxLoyaltyActivityLog,
  getLuxLoyaltyCustomerPendingPoints,
  getLuxLoyaltyOrderPendingPoints,
  getLuxLoyaltySuccessfulAccrualOnOrder,
  updateLuxLoyaltyAccountRegion,
};

export async function changeTier(params: TierChangeRequestBody): Promise<TierChangeResponse> {
  return request(`${BASE_PATH}/account/tier-change`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function getAllClaimCampaigns(): Promise<ListCampaignsResponse> {
  return request(`${BASE_PATH}/claims/campaigns/all`, { method: 'GET' });
}

export async function enrolAccount(customerId: string): Promise<unknown> {
  return request(`${BASE_PATH}/account`, {
    method: 'POST',
    body: JSON.stringify({ customerId }),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function adminClaim(
  campaignId: string,
  params: AdminSubmitClaimRequestBody,
): Promise<AdminSubmitClaimResponse> {
  return request(`${BASE_PATH}/claims/campaigns/${campaignId}/admin-claim`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export const luxLoyaltyRegions: Record<LuxLoyaltyAccountRegions, string> = {
  AU: 'Australia',
  CA: 'Canada',
  CN: 'China',
  FR: 'France',
  DE: 'Germany',
  HK: 'Hong Kong',
  IN: 'India',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IT: 'Italy',
  JP: 'Japan',
  KR: 'South Korea',
  MO: 'Macao',
  MY: 'Malaysia',
  NL: 'Netherlands',
  NZ: 'New Zealand',
  PH: 'Philippines',
  QA: 'Qatar',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  ZA: 'South Africa',
  ES: 'Spain',
  TW: 'Taiwan',
  TH: 'Thailand',
  AE: 'United Arab Emirates',
  GB: 'United Kingdom',
  US: 'United States',
  VN: 'Vietnam',
} as const;

export async function searchPromoCampaigns(
  params: PromoCampaignSearchParams,
  options: RequestOptions = {},
): Promise<PromoCampaignSearchResponse> {
  return request(`${BASE_PATH}/promo-campaigns/search`, {
    ...options,
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function createPromoCampaign(params: {
  campaign: PromoCampaignBody;
  userIds?: Array<string>;
}): Promise<PromoCampaignCreateResponse> {
  return request(`${BASE_PATH}/promo-campaigns`, {
    method: 'POST',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function readPromoCampaign(promoCampaignId: string): Promise<PromoCampaignReadResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'GET',
  });
}

export async function updatePromoCampaign(
  promoCampaignId: string,
  params: { campaign: PromoCampaignUpdateBody; addUserIds?: Array<string> },
): Promise<PromoCampaignUpdateResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'PATCH',
    body: JSON.stringify(params),
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function deletePromoCampaign(promoCampaignId: string): Promise<PromoCampaignDeleteResponse> {
  return request(`${BASE_PATH}/promo-campaigns/${promoCampaignId}`, {
    method: 'DELETE',
  });
}
